import './PerformanceIntelligenceChart.css';

export const PERFORMANCE_FIRMS = [
  { key: 'hrt', label: 'HRT', aliases: ['hrt', 'hudsonrivertrading'], color: '#007a4d', dash: '', marker: 'circle', fiscalYearEnd: '31 Dec' },
  { key: 'citadel', label: 'Citadel Securities', aliases: ['citadel', 'citadelsecurities'], color: '#0b4f8a', dash: '9 4', marker: 'square', fiscalYearEnd: '31 Dec' },
  { key: 'xtx', label: 'XTX', aliases: ['xtx', 'xtxmarkets'], color: '#8a4b08', dash: '3 3', marker: 'diamond', fiscalYearEnd: '31 Dec' },
  { key: 'jane', label: 'Jane Street', aliases: ['jane', 'janestreet'], color: '#cc2900', dash: '12 4 2 4', marker: 'triangle', fiscalYearEnd: '31 Dec' },
  { key: 'imc', label: 'IMC', aliases: ['imc', 'imctrading'], color: '#6b3fa0', dash: '14 5', marker: 'cross', fiscalYearEnd: '31 Dec' },
  { key: 'optiver', label: 'Optiver', aliases: ['optiver'], color: '#126e82', dash: '2 5', marker: 'ring', fiscalYearEnd: '31 Dec' },
];

const UNIT_SCALE = { unit: 1, units: 1, whole: 1, thousand: 1e3, million: 1e6, billion: 1e9 };
const CONFIDENCE_RANK = { confirmed: 5, reported: 4, estimated: 3, contested: 2, rumoured: 1 };
const BLOCKED_STATES = new Set(['rejected', 'quarantined', 'rework', 'failed', 'withdrawn']);

const normalizeName = value => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const finite = value => value === null || value === undefined || value === '' ? Number.NaN : Number(value);
const yearFrom = row => {
  const periodEnd = row?.period_end || row?.period_end_or_null;
  if (periodEnd) {
    const year = new Date(`${periodEnd}T00:00:00Z`).getUTCFullYear();
    if (Number.isFinite(year)) return year;
  }
  const years = String(row?.period || '').match(/(?:19|20)\d{2}/g)?.map(Number) || [];
  return years.length ? Math.max(...years) : 0;
};
const isFullYear = row => ['fiscal_year', 'full_year', 'annual', 'year'].includes(String(row?.period_type || '').toLowerCase())
  || /\bFY\s*\d{2,4}(?:A)?\b|full[ -]?year|year (?:ended|ending)/i.test(String(row?.period || ''));
const isApproved = row => row?.canonical === true
  && row?.publication_eligible === true
  && ['approved', 'published'].includes(String(row?.validation_status || '').toLowerCase())
  && !BLOCKED_STATES.has(String(row?.status || '').toLowerCase());
const isApprovedScenario = row => row?.record_type === 'outlook_scenario'
  && isApproved(row)
  && Boolean(row.finance_approved_at)
  && Boolean(row.formula_version)
  && Boolean(row.method_key || row.formula || row.method_summary);

const nativeAmount = (row, field = 'value') => {
  const value = finite(row?.[field]);
  if (!Number.isFinite(value)) return Number.NaN;
  const scale = UNIT_SCALE[String(row?.original_unit || '').toLowerCase()] || 1;
  return value * scale;
};

const normalizedAmount = (row, field = 'value') => {
  const currency = String(row?.original_currency || row?.currency || '').toUpperCase();
  if (currency === 'USD') return nativeAmount(row, field);

  const normalizedField = field === 'range_low'
    ? 'normalized_range_low_usd'
    : field === 'range_high'
      ? 'normalized_range_high_usd'
      : 'normalized_usd';
  const normalized = finite(row?.[normalizedField]);
  const method = row?.normalization_method || row?.transformation_method;
  const version = row?.normalization_version || row?.formula_version;
  return String(row?.normalization_status || '').toLowerCase() === 'approved'
    && Number.isFinite(normalized)
    && method
    && version
    ? normalized
    : Number.NaN;
};

const currencySymbol = currency => ({ USD: '$', EUR: '€', GBP: '£', AUD: 'A$', CAD: 'C$' }[currency] || `${currency || ''} `);
const compactBillions = value => `${Number(value.toFixed(value >= 10 ? 1 : 2))}B`;
const formatUsdBillions = value => Number.isFinite(value) ? `$${compactBillions(value)}` : '—';
const formatNative = row => {
  if (!row) return '—';
  const currency = String(row.original_currency || row.currency || '').toUpperCase();
  const format = amount => {
    if (!Number.isFinite(amount)) return '—';
    const sign = amount < 0 ? '-' : '';
    const absolute = Math.abs(amount);
    if (absolute >= 1e9) return `${sign}${currencySymbol(currency)}${compactBillions(absolute / 1e9)}`;
    if (absolute >= 1e6) return `${sign}${currencySymbol(currency)}${Number((absolute / 1e6).toFixed(1))}M`;
    return `${sign}${currencySymbol(currency)}${Number(absolute.toFixed(1)).toLocaleString()}`;
  };
  if (row.value_kind === 'range') return `${format(nativeAmount(row, 'range_low'))}–${format(nativeAmount(row, 'range_high'))}`;
  const qualifier = row.value_kind === 'lower_bound' ? '≥' : row.value_kind === 'upper_bound' ? '≤' : '';
  return `${qualifier}${format(nativeAmount(row))}`;
};

const rowTimestamp = row => {
  const candidate = row?.finance_approved_at || row?.source_reported_at || row?.reported_date || row?.updated_at || row?.created_at;
  const timestamp = candidate ? new Date(candidate).getTime() : 0;
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const pickLatest = rows => [...rows].sort((a, b) =>
  rowTimestamp(b) - rowTimestamp(a)
  || (CONFIDENCE_RANK[String(b.confidence || '').toLowerCase()] || 0) - (CONFIDENCE_RANK[String(a.confidence || '').toLowerCase()] || 0)
)[0] || null;

const sortByPeriod = rows => [...rows].sort((a, b) =>
  new Date(`${a.period_end || '1970-01-01'}T00:00:00Z`).getTime()
  - new Date(`${b.period_end || '1970-01-01'}T00:00:00Z`).getTime()
  || rowTimestamp(a) - rowTimestamp(b)
);

const resolveFirmKey = (row, competitorKeyById) => {
  const byId = competitorKeyById.get(String(row?.competitor_id));
  if (byId) return byId;
  const candidates = [row?.firm_name, row?.competitor_name, row?.competitor_id].map(normalizeName).filter(Boolean);
  return PERFORMANCE_FIRMS.find(firm => candidates.some(candidate => firm.aliases.includes(candidate)))?.key || null;
};

export const getRollingQuarterSlots = (targetYear, yearCount = 5) => {
  const startYear = targetYear - yearCount + 1;
  return Array.from({ length: yearCount * 4 }, (_, index) => ({
    index,
    year: startYear + Math.floor(index / 4),
    quarter: (index % 4) + 1,
    id: `${startYear + Math.floor(index / 4)}-Q${(index % 4) + 1}`,
  }));
};

const observationPoint = row => {
  if (!row) return null;
  const amount = normalizedAmount(row);
  if (!Number.isFinite(amount)) return null;
  return {
    row,
    year: yearFrom(row),
    value: amount / 1e9,
    nativeLabel: formatNative(row),
    usdLabel: formatUsdBillions(amount / 1e9),
    confidence: row.confidence || 'unknown',
    sourceUrl: row.source_url || null,
    source: row.source || 'Source',
  };
};

const scenarioValue = row => {
  const kind = String(row?.value_kind || '').toLowerCase();
  if (kind === 'range') {
    const low = normalizedAmount(row, 'range_low');
    const high = normalizedAmount(row, 'range_high');
    if (!Number.isFinite(low) || !Number.isFinite(high) || low > high) return null;
    return { row, kind, low: low / 1e9, high: high / 1e9, point: null };
  }
  const amount = normalizedAmount(row);
  if (!Number.isFinite(amount)) return null;
  const explicitLow = finite(row.uncertainty_low_usd);
  const explicitHigh = finite(row.uncertainty_high_usd);
  return {
    row,
    kind,
    point: kind === 'point' ? amount / 1e9 : null,
    low: kind === 'lower_bound' ? amount / 1e9 : Number.isFinite(explicitLow) ? explicitLow / 1e9 : null,
    high: kind === 'upper_bound' ? amount / 1e9 : Number.isFinite(explicitHigh) ? explicitHigh / 1e9 : null,
  };
};

const scenarioLabel = scenario => {
  if (!scenario) return '—';
  if (scenario.kind === 'range') return `${formatUsdBillions(scenario.low)}–${formatUsdBillions(scenario.high)}`;
  if (scenario.kind === 'lower_bound') return `≥${formatUsdBillions(scenario.low)}`;
  if (scenario.kind === 'upper_bound') return `≤${formatUsdBillions(scenario.high)}`;
  return formatUsdBillions(scenario.point);
};

const quarterFromDate = (value, fallbackQuarter) => {
  if (!value) return fallbackQuarter;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? fallbackQuarter : Math.floor(date.getUTCMonth() / 3) + 1;
};

export function buildPerformanceDataset({ competitors = [], observations = [], scenarios = [], targetYear, asOfDate = new Date() }) {
  const slots = getRollingQuarterSlots(targetYear);
  const startYear = slots[0].year;
  const competitorKeyById = new Map();
  competitors.forEach(competitor => {
    const normalized = normalizeName(competitor.name);
    const firm = PERFORMANCE_FIRMS.find(item => item.aliases.includes(normalized));
    if (firm) competitorKeyById.set(String(competitor.id), firm.key);
  });

  const approvedObservations = observations.filter(row => row?.record_type === 'observation' && isApproved(row));
  const currentQuarter = quarterFromDate(asOfDate, 4);

  const series = PERFORMANCE_FIRMS.map(firm => {
    const firmRows = approvedObservations.filter(row => resolveFirmKey(row, competitorKeyById) === firm.key);
    const annual = new Map();
    const withheld = new Map();

    for (let year = startYear; year <= targetYear - 1; year += 1) {
      const candidates = firmRows.filter(row => yearFrom(row) === year && isFullYear(row));
      const selected = pickLatest(candidates);
      if (!selected) continue;
      const point = observationPoint(selected);
      if (point) annual.set(year, point);
      else withheld.set(year, selected);
    }

    const firmScenarios = scenarios.filter(row => resolveFirmKey(row, competitorKeyById) === firm.key
      && yearFrom(row) === targetYear
      && isApprovedScenario(row));
    const selectedScenarioRow = pickLatest(firmScenarios);
    const scenario = scenarioValue(selectedScenarioRow);
    const scenarioQuarter = quarterFromDate(
      selectedScenarioRow?.scenario_as_of || selectedScenarioRow?.input_period_end || selectedScenarioRow?.source_reported_at,
      currentQuarter
    );
    const contexts = sortByPeriod(firmRows.filter(row => yearFrom(row) === targetYear && !isFullYear(row)));
    const context = contexts[contexts.length - 1] || null;

    return { ...firm, annual, withheld, scenario, scenarioQuarter, context, contexts };
  });

  const annualCount = series.reduce((total, firm) => total + firm.annual.size, 0);
  const currentScenarioCount = series.filter(firm => firm.scenario).length;
  const withheldCount = series.reduce((total, firm) => total + firm.withheld.size, 0);
  const values = series.flatMap(firm => [
    ...[...firm.annual.values()].map(point => point.value),
    firm.scenario?.point,
    firm.scenario?.low,
    firm.scenario?.high,
  ]).filter(Number.isFinite);
  const observedMax = values.length ? Math.max(...values) : 60;
  const yMax = Math.max(10, Math.ceil(observedMax / 10) * 10);

  return { slots, series, annualCount, currentScenarioCount, withheldCount, yMax, startYear, targetYear };
}

const Marker = ({ type, x, y, color, hollow = false, size = 5 }) => {
  const fill = hollow ? '#fff' : color;
  if (type === 'square') return <rect x={x-size} y={y-size} width={size*2} height={size*2} rx="1" fill={fill} stroke={color} strokeWidth="2"/>;
  if (type === 'diamond') return <path d={`M ${x} ${y-size-1} L ${x+size+1} ${y} L ${x} ${y+size+1} L ${x-size-1} ${y} Z`} fill={fill} stroke={color} strokeWidth="2"/>;
  if (type === 'triangle') return <path d={`M ${x} ${y-size-2} L ${x+size+1} ${y+size} L ${x-size-1} ${y+size} Z`} fill={fill} stroke={color} strokeWidth="2"/>;
  if (type === 'cross') return <path d={`M ${x-size} ${y-size} L ${x+size} ${y+size} M ${x+size} ${y-size} L ${x-size} ${y+size}`} fill="none" stroke={color} strokeWidth="2.5"/>;
  if (type === 'ring') return <circle cx={x} cy={y} r={size} fill="#fff" stroke={color} strokeWidth="3"/>;
  return <circle cx={x} cy={y} r={size} fill={fill} stroke={color} strokeWidth="2"/>;
};

const ChartSvg = ({ dataset }) => {
  const width = 980;
  const height = 410;
  const margin = { top: 24, right: 26, bottom: 62, left: 56 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const x = index => margin.left + (index / (dataset.slots.length - 1)) * plotWidth;
  const y = value => margin.top + plotHeight - (value / dataset.yMax) * plotHeight;
  const yTicks = Array.from({ length: 5 }, (_, index) => (dataset.yMax / 4) * index);
  const slotIndex = (year, quarter) => dataset.slots.findIndex(slot => slot.year === year && slot.quarter === quarter);

  return (
    <svg className="performance-chart-svg" viewBox={`0 0 ${width} ${height}`} role="img" aria-labelledby="performance-chart-title performance-chart-desc">
      <title id="performance-chart-title">Five-year annualised net trading income comparison</title>
      <desc id="performance-chart-desc">Approved annual observations are plotted at each fiscal year end. The current year uses separately approved annualised scenarios and uncertainty envelopes. Missing years remain gaps.</desc>
      <defs>
        <pattern id="scenario-hatch" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="8" stroke="#011735" strokeOpacity=".28" strokeWidth="2"/>
        </pattern>
      </defs>

      {yTicks.map(value => (
        <g key={value}>
          <line x1={margin.left} x2={width-margin.right} y1={y(value)} y2={y(value)} stroke="#cfe3f4" strokeWidth="1"/>
          <text x={margin.left-10} y={y(value)+4} textAnchor="end" className="performance-chart-axis">${Number(value.toFixed(0))}B</text>
        </g>
      ))}

      {dataset.slots.map((slot, index) => (
        <g key={slot.id}>
          {slot.quarter === 1 && <line x1={x(index)} x2={x(index)} y1={margin.top} y2={margin.top+plotHeight} stroke="#9dbbd2" strokeDasharray="2 4"/>}
          <text x={x(index)} y={height-36} textAnchor="middle" className="performance-chart-quarter">Q{slot.quarter}</text>
          {slot.quarter === 2 && <text x={(x(index-1)+x(index+2))/2} y={height-12} textAnchor="middle" className="performance-chart-year">{slot.year}{slot.year === dataset.targetYear ? 'E' : ''}</text>}
        </g>
      ))}

      {dataset.series.map(firm => {
        const actualPoints = [...firm.annual.values()].sort((a, b) => a.year - b.year);
        const segments = [];
        for (let index = 1; index < actualPoints.length; index += 1) {
          if (actualPoints[index].year - actualPoints[index-1].year === 1) segments.push([actualPoints[index-1], actualPoints[index]]);
        }
        const lastActual = actualPoints[actualPoints.length-1];
        const scenarioIndex = slotIndex(dataset.targetYear, firm.scenarioQuarter);
        const scenarioX = scenarioIndex >= 0 ? x(scenarioIndex) : x(dataset.slots.length-1);
        const lowerBoundConnector = lastActual?.year === dataset.targetYear - 1
          && firm.scenario?.kind === 'lower_bound'
          && Number.isFinite(firm.scenario.low);
        if (lastActual?.year === dataset.targetYear - 1 && Number.isFinite(firm.scenario?.point)) {
          segments.push([lastActual, { year: dataset.targetYear, value: firm.scenario.point, scenarioX }]);
        }
        return (
          <g key={firm.key} data-series={firm.key}>
            {segments.map(([start, end], index) => (
              <line key={index}
                data-testid={`${firm.key}-${start.year}-${end.year}-segment`}
                x1={x(slotIndex(start.year, 4))} y1={y(start.value)}
                x2={end.scenarioX ?? x(slotIndex(end.year, 4))} y2={y(end.value)}
                stroke={firm.color} strokeWidth="2.5" strokeDasharray={firm.dash || undefined}/>
            ))}
            {lowerBoundConnector && <line
              data-testid={`${firm.key}-${dataset.targetYear-1}-${dataset.targetYear}-lower-bound-segment`}
              x1={x(slotIndex(lastActual.year, 4))} y1={y(lastActual.value)}
              x2={scenarioX} y2={y(firm.scenario.low)}
              stroke={firm.color} strokeWidth="2.5" strokeDasharray="4 5" opacity=".78"/>
            }
            {actualPoints.map(point => (
              <g key={point.year} data-testid={`${firm.key}-${point.year}-actual`}>
                <title>{firm.label} {point.year}: {point.usdLabel} annual NTI, {point.confidence}. Native value {point.nativeLabel}.</title>
                <Marker type={firm.marker} x={x(slotIndex(point.year, 4))} y={y(point.value)} color={firm.color}/>
              </g>
            ))}
            {firm.scenario && <g data-testid={`${firm.key}-${dataset.targetYear}-scenario`} data-scenario-kind={firm.scenario.kind}>
              <title>{firm.label} {dataset.targetYear} annualised scenario: {scenarioLabel(firm.scenario)}. This is a scenario, not a reported full-year result.</title>
              {Number.isFinite(firm.scenario.low) && Number.isFinite(firm.scenario.high) && <>
                <rect x={scenarioX-9} y={y(firm.scenario.high)} width="18" height={Math.max(3,y(firm.scenario.low)-y(firm.scenario.high))} fill="url(#scenario-hatch)" stroke={firm.color} strokeWidth="1.5"/>
                <line x1={scenarioX-13} x2={scenarioX+13} y1={y(firm.scenario.low)} y2={y(firm.scenario.low)} stroke={firm.color} strokeWidth="1.5"/>
                <line x1={scenarioX-13} x2={scenarioX+13} y1={y(firm.scenario.high)} y2={y(firm.scenario.high)} stroke={firm.color} strokeWidth="1.5"/>
              </>}
              {Number.isFinite(firm.scenario.point) && <Marker type={firm.marker} x={scenarioX} y={y(firm.scenario.point)} color={firm.color} hollow size={6}/>}
              {firm.scenario.kind === 'lower_bound' && <>
                <Marker type={firm.marker} x={scenarioX} y={y(firm.scenario.low)} color={firm.color} hollow size={6}/>
                <path d={`M ${scenarioX} ${y(firm.scenario.low)-5} L ${scenarioX} ${y(firm.scenario.low)-24} M ${scenarioX-4} ${y(firm.scenario.low)-20} L ${scenarioX} ${y(firm.scenario.low)-25} L ${scenarioX+4} ${y(firm.scenario.low)-20}`} fill="none" stroke={firm.color} strokeWidth="2"/>
              </>}
              {firm.scenario.kind === 'upper_bound' && <Marker type={firm.marker} x={scenarioX} y={y(firm.scenario.high)} color={firm.color} hollow size={6}/>}
            </g>}
          </g>
        );
      })}
      <text x={14} y={margin.top + plotHeight/2} transform={`rotate(-90 14 ${margin.top + plotHeight/2})`} textAnchor="middle" className="performance-chart-unit">Annualised NTI · USD billions</text>
    </svg>
  );
};

const LegendMarker = ({ firm }) => (
  <svg width="34" height="14" viewBox="0 0 34 14" aria-hidden="true">
    <line x1="1" x2="33" y1="7" y2="7" stroke={firm.color} strokeWidth="2" strokeDasharray={firm.dash || undefined}/>
    <Marker type={firm.marker} x={17} y={7} color={firm.color} size={3.5}/>
  </svg>
);

const valueCell = (firm, year, targetYear) => {
  if (year === targetYear) {
    if (!firm.scenario) return <><span className="performance-missing">—</span><small>No approved annualised scenario</small></>;
    const envelope = Number.isFinite(firm.scenario.low) && Number.isFinite(firm.scenario.high)
      ? ` · envelope ${formatUsdBillions(firm.scenario.low)}–${formatUsdBillions(firm.scenario.high)}`
      : '';
    return <><strong>{scenarioLabel(firm.scenario)}</strong><small>Annualised scenario{envelope} · {firm.scenarioQuarter === 4 ? 'current' : `as of Q${firm.scenarioQuarter}`}</small></>;
  }
  const point = firm.annual.get(year);
  if (point) return <><strong>{point.usdLabel}</strong><small>{point.nativeLabel === point.usdLabel ? `${point.confidence}` : `${point.nativeLabel} · ${point.confidence}`}</small></>;
  if (firm.withheld.has(year)) return <><span className="performance-missing">—</span><small>Native observation; approved FX missing</small></>;
  return <><span className="performance-missing">—</span><small>No approved observation</small></>;
};

const EvidenceNotes = ({ dataset }) => {
  const currentAnchors = dataset.series.flatMap(firm =>
    firm.contexts.map(row => ({ firm, row }))
  );
  const evidenceRows = dataset.series.flatMap(firm => [
    ...[...firm.annual.values()].map(point => ({ firm, row: point.row, label: `${point.year} ${point.usdLabel}` })),
    ...(firm.scenario ? [{ firm, row: firm.scenario.row, label: `${dataset.targetYear}E ${scenarioLabel(firm.scenario)}` }] : []),
  ]);
  return (
    <div className="performance-notes">
      {currentAnchors.length > 0 && <div className="performance-current-anchors">
        <strong>Current-year evidence anchors (context only)</strong>
        <ul>
          {currentAnchors.map(({ firm, row }) => <li key={`${firm.key}-${row.publication_key || row.period}`}>
            <span>{firm.label}: {row.period || 'partial period'} {formatNative(row)} · {row.confidence || 'confidence not stated'}</span>
            {row.source_url && <> · <a href={row.source_url} target="_blank" rel="noreferrer">evidence ↗</a></>}
            {row.evidence_locator && <small>{row.evidence_locator}</small>}
          </li>)}
        </ul>
      </div>}
      <details>
        <summary>Evidence, FX and methodology ({evidenceRows.length})</summary>
        <ul>
          {evidenceRows.map(({ firm, row, label }, index) => {
            const nativeCurrency = String(row.original_currency || row.currency || '').toUpperCase();
            const fx = nativeCurrency && nativeCurrency !== 'USD'
              ? ` · FX: ${row.normalization_method || 'method unavailable'}${row.normalization_version || row.formula_version ? ` (${row.normalization_version || row.formula_version})` : ''}${row.fx_rate ? ` @ ${row.fx_rate}` : ''}`
              : '';
            return <li key={`${firm.key}-${label}-${index}`}>
              <strong>{firm.label} {label}</strong> · {row.period || 'period unavailable'} · {row.entity_scope || 'scope unavailable'} · {row.definition_variant || 'definition unavailable'}{fx}
              {row.source_url && <> · <a href={row.source_url} target="_blank" rel="noreferrer">{row.source || 'evidence'} ↗</a></>}
              {row.method_summary && <small>{row.method_summary}</small>}
            </li>;
          })}
        </ul>
      </details>
      <p><strong>Method.</strong> Reported annual observations are plotted at Q4 because all six firms currently use a 31 December fiscal year-end. Non-USD flows require an approved period-average FX transformation with method lineage. The {dataset.targetYear} layer is annualised and hatched; it is not a reported full-year result. Lines stop at missing annual periods, and point, bound and range scenarios are not assigned invented midpoints.</p>
      <p><strong>Comparability.</strong> “NTI” can mean net trading income, net trading revenue or a disclosed trading-revenue proxy. Entity scope and definition remain attached to each point; XTX entity combinations and other material scope differences must be reviewed before rank-like use.</p>
    </div>
  );
};

export default function PerformanceIntelligenceChart({
  competitors = [],
  observations = [],
  scenarios = [],
  targetYear = new Date().getFullYear(),
  asOfDate = new Date(),
  onSelectCompetitor = () => {},
}) {
  const dataset = buildPerformanceDataset({ competitors, observations, scenarios, targetYear, asOfDate });
  const competitorByFirm = new Map();
  competitors.forEach(competitor => {
    const firm = PERFORMANCE_FIRMS.find(item => item.aliases.includes(normalizeName(competitor.name)));
    if (firm) competitorByFirm.set(firm.key, competitor);
  });

  return (
    <section className="performance-intelligence" aria-labelledby="performance-intelligence-heading">
      <div className="performance-intelligence-heading">
        <div>
          <h3 id="performance-intelligence-heading">Five-year annualised NTI</h3>
          <p>{dataset.startYear}–{dataset.targetYear} · calendar-year view · USD billions</p>
        </div>
        <span className="performance-asof">{targetYear} is annualised</span>
      </div>

      <p id="performance-intelligence-summary" className="performance-summary">
        {dataset.annualCount} approved annual anchors across six firms; {dataset.currentScenarioCount} approved {targetYear} scenario{dataset.currentScenarioCount === 1 ? '' : 's'}. {dataset.withheldCount > 0 ? `${dataset.withheldCount} native-currency observation${dataset.withheldCount === 1 ? '' : 's'} withheld from the USD scale pending approved FX.` : 'Missing periods remain gaps.'}
      </p>

      <div className="performance-legend" role="group" aria-label="Firm series legend">
        {dataset.series.map(firm => (
          <button key={firm.key} type="button" onClick={() => competitorByFirm.has(firm.key) && onSelectCompetitor(competitorByFirm.get(firm.key))}>
            <LegendMarker firm={firm}/><span>{firm.label}</span>
          </button>
        ))}
        <span className="performance-scenario-key"><i/>Hatched = scenario envelope</span>
      </div>

      <div className="performance-chart-frame" aria-describedby="performance-intelligence-summary">
        <ChartSvg dataset={dataset}/>
      </div>

      <div className="performance-table-wrap">
        <table>
          <caption>Accessible table of approved annual NTI observations and current-year annualised scenarios</caption>
          <thead><tr><th scope="col">Firm / FY end</th>{dataset.slots.filter(slot => slot.quarter === 4).map(slot => <th scope="col" key={slot.year}>{slot.year}{slot.year === targetYear ? 'E' : ''}</th>)}</tr></thead>
          <tbody>{dataset.series.map(firm => <tr key={firm.key}>
            <th scope="row"><button type="button" onClick={() => competitorByFirm.has(firm.key) && onSelectCompetitor(competitorByFirm.get(firm.key))}>{firm.label}</button><small>FY end {firm.fiscalYearEnd}</small></th>
            {dataset.slots.filter(slot => slot.quarter === 4).map(slot => <td key={slot.year}>{valueCell(firm, slot.year, targetYear)}</td>)}
          </tr>)}</tbody>
        </table>
      </div>

      <EvidenceNotes dataset={dataset}/>
    </section>
  );
}
